import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { LoadingButton } from '@mui/lab';
import { Box, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Field, FieldGrid, SectionCard } from '../../../components/Section';
import { usePermissions } from '../../../hooks';
import { useConfirmTreasuryKycMutation } from '../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryCompanySetup } from '../../../types/treasury';
import * as permissions from '../../../utils/permissions';
import { getTreasuryErrorMessage } from '../../../utils/treasury';

type TreasuryConfirmKycStepProps = {
  setup: TreasuryCompanySetup;
  onDone: () => void;
};

/**
 * Step 5 — confirm the owner's KYC. Approve copies the onboarding KYC to the owner's account but leaves its confirm
 * date empty (only Nafath and the app's confirm screen set it), and the owner's account can't turn Active without it:
 * signing the owner's agreement would leave them Pending. Confirming sets the date once; a confirmed KYC keeps it.
 */
export const TreasuryConfirmKycStep = ({ setup, onDone }: TreasuryConfirmKycStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canConfirm = usePermissions({ items: [permissions.Treasury.approveEmployee] });
  const [confirmTreasuryKyc, { isLoading: isConfirming }] = useConfirmTreasuryKycMutation();

  const confirm = async () => {
    if (!setup.employeeId) return;

    try {
      const response = await confirmTreasuryKyc({ companyId: setup.companyId, employeeId: setup.employeeId }).unwrap();
      enqueueSnackbar(response?.alreadyConfirmed ? 'KYC was already confirmed.' : 'KYC confirmed!', {
        variant: 'success',
      });
      onDone();
    } catch (error) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'KYC confirm error!'), { variant: 'error' });
    }
  };

  return (
    <SectionCard title="Owner's KYC" description="Check the owner's details, then confirm their KYC.">
      <Stack spacing={3}>
        <FieldGrid>
          <Field label="Full Name (English)" value={setup.employee?.full_name_en} />
          <Field label="Full Name (Arabic)" value={setup.employee?.full_name_ar} />
          <Field label="Work Email" value={setup.employee?.email} />
          <Field label="National ID" value={setup.employee?.national_id} />
        </FieldGrid>
        <Typography variant="caption" color="text.secondary">
          Confirming records that the owner&apos;s KYC was reviewed. It is needed before the owner&apos;s agreement is
          signed, or the owner&apos;s account stays Pending.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={isConfirming}
            startIcon={<VerifiedUserIcon />}
            onClick={confirm}
            disabled={isConfirming || !canConfirm}
          >
            Confirm KYC
          </LoadingButton>
        </Box>
      </Stack>
    </SectionCard>
  );
};
